import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertBody, AlertDescription, AlertIcon, AlertTitle } from '../../../design-system/alert';
import { useCurrentCompany } from '../../../lib/who-am-i';
import { useUserCountMetadata } from '../../../queries/users';
import { GroupType } from '../../../schemas/groups';
import { ClaimLimitPeriod } from '../../../schemas/incentives';

type BudgetEstimatePeriodProps = {
	amount: number;
	conditions: { attr: string | undefined; value: string | undefined }[];
	operator: GroupType;
	period: ClaimLimitPeriod;
	quantity: number | null;
	userClaimLimit: number | null;
};

export const BudgetEstimatePeriod: FC<BudgetEstimatePeriodProps> = ({ amount, conditions, operator, period, quantity, userClaimLimit }) => {
	const { t } = useTranslation('incentives', { keyPrefix: 'form' });
	const company = useCurrentCompany();

	const filteredConditions = useMemo(() => {
		return conditions.filter((condition) => condition.attr && condition.value) as { attr: string; value: string }[];
	}, [conditions]);

	const { data } = useUserCountMetadata({ conditions: filteredConditions, operator, status: 'active' });

	if (!data || !amount || !quantity) {
		return null;
	}

	const totalQuantity = Math.min(quantity, data.scopedCount * (typeof userClaimLimit === 'number' && !isNaN(userClaimLimit) ? userClaimLimit : Infinity));
	const totalPoints = totalQuantity * amount;
	const totalDollars = Math.trunc(totalPoints / company.budget.exchange_rate);
	const companyCurrency = company.budget?.iso_code || 'USD';

	return (
		<Alert status="info" size="sm">
			<AlertIcon />
			<AlertBody>
				<AlertTitle>
					{t('estimateTitle', {
						value: totalDollars,
						currency: companyCurrency,
						maximumFractionDigits: 0,
						context: period,
					})}
				</AlertTitle>
				<AlertDescription>
					{t('estimatePerPeriod', { count: totalQuantity, context: period })} {t('estimateTotal', { count: totalPoints, context: period })}
				</AlertDescription>
			</AlertBody>
		</Alert>
	);
};
